// Små skjemadeler for «Tjenester og priser», i startsidens stil (ny-kundesamhandling/Skjema.jsx).
import React from 'react';
import { feltKlasse } from '../../ny-kundesamhandling/Skjema';

const valgKnapp = (valgt) =>
  `min-h-[2.25rem] rounded-xl border-2 px-3 py-1.5 font-semibold transition touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 disabled:cursor-not-allowed disabled:opacity-60 ${
    valgt ? 'border-emerald-700 bg-emerald-700 text-white' : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50'
  }`;

// Flere valg som av/på-knapper (aria-pressed), f.eks. bookingtyper eller dager.
export function Flervalg({ id, etikett, hjelp, valg, verdi: valgte, onEndre, disabled }) {
  const verdi = valgte || [];
  const bytt = (v) => onEndre(verdi.includes(v) ? verdi.filter((x) => x !== v) : [...verdi, v]);
  return (
    <div role="group" aria-labelledby={id} aria-describedby={hjelp ? `${id}-hjelp` : undefined} className="min-w-0 space-y-1.5">
      <span id={id} className="block font-semibold text-slate-800">{etikett}</span>
      <div className="flex flex-wrap gap-2">
        {valg.map(([v, navn]) => (
          <button key={v} type="button" aria-pressed={verdi.includes(v)} disabled={disabled} onClick={() => bytt(v)} className={valgKnapp(verdi.includes(v))}>
            {navn}
          </button>
        ))}
      </div>
      {hjelp && <p id={`${id}-hjelp`} className="text-sm text-slate-600">{hjelp}</p>}
    </div>
  );
}

// Ja/nei som avkrysningsboks med tekst ved siden av.
export function Avkrysning({ id, etikett, hjelp, verdi, onEndre, disabled }) {
  return (
    <div className="flex min-w-0 items-start gap-2">
      <input
        id={id}
        type="checkbox"
        checked={!!verdi}
        disabled={disabled}
        onChange={(e) => onEndre(e.target.checked)}
        aria-describedby={hjelp ? `${id}-hjelp` : undefined}
        className="mt-1 h-6 w-6 shrink-0 rounded border-2 border-slate-400 accent-emerald-700"
      />
      <div className="min-w-0">
        <label htmlFor={id} className="font-semibold text-slate-800">{etikett}</label>
        {hjelp && <p id={`${id}-hjelp`} className="text-sm text-slate-600">{hjelp}</p>}
      </div>
    </div>
  );
}

// Heltall (pris, minutter). Tomt felt gir null.
export function Tallfelt({ id, etikett, hjelp, verdi, onEndre, enhet, tillatMinus = false, className = '', ...rest }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <label htmlFor={id} className="mb-1 block font-semibold text-slate-800">{etikett}</label>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="text"
          inputMode={tillatMinus ? 'text' : 'numeric'}
          autoComplete="off"
          value={verdi ?? ''}
          onChange={(e) => {
            const t = e.target.value.replace(tillatMinus ? /[^\d-]/g : /\D/g, '');
            onEndre(t === '' ? null : t === '-' ? '-' : Number(t));
          }}
          aria-describedby={hjelp ? `${id}-hjelp` : undefined}
          className={`${feltKlasse} max-w-[10rem]`}
          {...rest}
        />
        {enhet && <span className="text-slate-700">{enhet}</span>}
      </div>
      {hjelp && <p id={`${id}-hjelp`} className="mt-1 text-sm text-slate-600">{hjelp}</p>}
    </div>
  );
}

export function Melding({ type = 'info', children }) {
  const stil = {
    feil: 'border-red-200 bg-red-50 text-red-800',
    ok: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    info: 'border-slate-200 bg-slate-50 text-slate-800',
  }[type];
  return (
    <p role={type === 'feil' ? 'alert' : 'status'} className={`rounded-xl border px-3 py-1.5 ${stil}`}>
      {children}
    </p>
  );
}

// «Endret av …» og hele loggen, foldet sammen.
export function Endringslogg({ endringer = [] }) {
  if (!endringer.length) return null;
  return (
    <details className="rounded-xl border border-slate-200 bg-white p-2.5">
      <summary className="cursor-pointer font-semibold text-slate-800">Endringslogg ({endringer.length})</summary>
      <ul className="mt-2 space-y-1 text-sm text-slate-700">
        {[...endringer].reverse().map((e, i) => (
          <li key={i} className="[overflow-wrap:anywhere]">
            {new Date(e.dato).toLocaleString('nb-NO', { dateStyle: 'short', timeStyle: 'short' })} · {e.utfortAv || 'ukjent'}:{' '}
            {e.felt === 'opprettet' || e.felt === 'migrert fra Hygraph'
              ? e.felt
              : <>{e.felt} «{e.fra || 'tomt'}» → «{e.til || 'tomt'}»</>}
          </li>
        ))}
      </ul>
    </details>
  );
}
